/**
 * *Na sekeru* — the one rule two screens both need, and the narrow read of a tab that goes with it.
 *
 * §9.148 — the owner's point 42b puts *put it on my tab* in the payment dialog, which lives in
 * `manager-orders`, while the tabs screen lives in `manager-kasa`. Neither module depends on the
 * other, and **the definition of an open tab must not be written twice**: `manager-kasa` keeps its
 * full parser for the tabs screen and takes `TAB_OPEN`/`isOpenStatus` from here, so a change to what
 * *open* means reaches both.
 *
 * `openTabChoices` is deliberately a narrower read than `manager-kasa`'s `tabsOf`: a payment dialog
 * picking a tab needs its id, its name and what it already owes. It has no use for the tab's orders
 * or its settlements, and parsing them to throw them away would be the second parser this file
 * exists to avoid.
 */

/** The status the server writes while a tab is still running. */
export const TAB_OPEN = 'open'

export function isOpenStatus(status: unknown): boolean {
  return status === TAB_OPEN
}

/** A tab as a chooser needs it: who it belongs to, and what is on it already. */
export interface TabChoice {
  id: number
  /** The tab's own name; never blank, because a nameless row cannot be picked from a list. */
  name: string
  outstandingCents: number
}

function cents(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value * 100)
  if (typeof value === 'string' && value !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0
  }
  return 0
}

/**
 * The open tabs, in the order the server sent them — oldest first, which is the order they need
 * chasing in and a decision the backend made on purpose. Re-sorting here would undo it.
 */
export function openTabChoices(payload: unknown): TabChoice[] {
  if (!Array.isArray(payload)) return []
  const choices: TabChoice[] = []
  for (const raw of payload) {
    if (typeof raw !== 'object' || raw === null) continue
    const row = raw as Record<string, unknown>
    if (!isOpenStatus(row.status)) continue
    if (typeof row.id !== 'number' || !Number.isFinite(row.id)) continue
    const name = typeof row.name === 'string' ? row.name.trim() : ''
    choices.push({
      id: row.id,
      // A tab with no name still has to be pickable — it is somebody's money either way.
      name: name === '' ? `#${row.id}` : name,
      outstandingCents: cents(row.outstanding),
    })
  }
  return choices
}

/**
 * Why a bill cannot go on the slate, or `null` when it can.
 *
 * The gate is the channel's `allowsDeferredPayment`, enforced by `OrderTabService.append`. Measured
 * on this data it is true only for *Na sekeru*, so a table bill is refused by the server whatever the
 * screen offers — which is `OWNER-QUESTIONS.md` §9, a switch on *Predajné kanály* rather than a bug.
 *
 * **Said rather than hidden.** An option that silently vanishes cannot be told apart from one that
 * was never built, and that is exactly the report this would otherwise produce.
 */
export function tabRefusal(
  channelName: string | undefined,
  allowsDeferred: boolean,
  /**
   * Whether the `na_sekeru` channel is running at all — §9.281, owner O20b.
   *
   * **A different question from the one above, and it is asked first.** `allowsDeferred` is about
   * *this order's* channel: `restaurant` permits a deferred payment, which is why the button showed
   * while the whole feature was switched off. If `na_sekeru` is off the reason has nothing to do with
   * this order, so saying *"«Reštaurácia» nemá povolenú odloženú platbu"* would send somebody to the
   * wrong switch.
   *
   * Defaults to `true` so a caller that has not read the channels behaves exactly as before.
   */
  naSekeruOn = true,
): string | null {
  if (!naSekeruOn) {
    return 'Kanál „Na sekeru“ je vypnutý - zapnite ho v Predajných kanáloch.'
  }
  if (allowsDeferred) return null
  const named = channelName === undefined || channelName === '' ? 'Tento kanál' : `„${channelName}“`
  return `${named} nemá povolenú odloženú platbu - zapnite ju v Predajných kanáloch.`
}

/** The slug of the channel the whole slate feature exists for. */
export const NA_SEKERU = 'na_sekeru'

/**
 * Whether the slate is offered at all — §9.281, owner O20 and O20b.
 *
 * The one predicate both places consult: the sidebar entry and the payment dialog. The owner's two
 * messages are the same rule seen from two screens, and answering only the first would have left the
 * second outstanding by construction.
 *
 * **Absent is on.** A caller that has not loaded the channels yet, or a franchise whose list does not
 * mention the slug, keeps the behaviour that shipped — a feature must not vanish because a request is
 * in flight.
 */
export function naSekeruEnabled(
  channels: readonly { slug?: string | null; isActive?: boolean | null }[] | undefined,
): boolean {
  return channelActive(channels, NA_SEKERU)
}

/**
 * Whether a sales channel is switched on — the primitive `naSekeruEnabled` and the sidebar's
 * `isChannelActive` both call.
 *
 * **It exists because the first cut of §9.281 had two.** The payment dialog used `naSekeruEnabled`
 * and `AccessContext` grew its own copy reading the same payload, so a mutation to one left the
 * other passing — measured: the sidebar's regression test survived breaking the helper it was
 * supposed to depend on. The entry claimed *"one predicate, read in both places"* and the code had
 * two.
 */
export function channelActive(
  channels: readonly { slug?: string | null; isActive?: boolean | null }[] | undefined,
  slug: string,
): boolean {
  if (channels === undefined) return true
  const found = channels.find((channel) => channel.slug === slug)
  if (found === undefined) return true
  return found.isActive !== false
}
